import React, { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useFeatureFlagContext } from '../contexts/FeatureFlagContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@/icons';
import { CustomToggle } from '../components/CustomToggle';
import { TermsAndConditionsContent } from '../components/TermsAndConditionsContent';
import { SignupProgress } from '../components/SignupProgress';
import { authApiService } from '../services/authApiService';
import { debugError } from '../utils/debug';

/**
 * OAuth signup final step: accept T&C and promote to full user (no password required).
 */
export const RegisterCompletePage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { flags, isLoading: flagsLoading } = useFeatureFlagContext();
    const selfServeSignup = flags['SelfServiceSignup'] ?? false;

    const token = (searchParams.get('token') || '').trim();
    const [status, setStatus] = useState<'terms' | 'submitting' | 'done' | 'err'>('terms');
    const [message, setMessage] = useState<string | null>(null);
    const [acceptTerms, setAcceptTerms] = useState(false);

    useEffect(() => {
        document.title = 'Accept Terms | Calculist';
    }, []);

    if (flagsLoading) return null;
    if (!selfServeSignup) return <Navigate to="/auth/login" replace />;

    const wrapper = (content: React.ReactNode) => (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.md,
        }}>
            {content}
        </div>
    );

    if (!token) {
        return wrapper(
            <div style={themedStyles.card.default}>
                <p style={themedStyles.text.body}>Invalid link. Missing verification token.</p>
                <Link to="/auth/register" style={{ color: theme.colors.primary }}>Start registration</Link>
            </div>
        );
    }

    const handleAccept = async () => {
        if (!acceptTerms) return;
        setStatus('submitting');
        try {
            await authApiService.signupRegisterPromoteOAuth(token);
            setStatus('done');
        } catch (err: unknown) {
            debugError('signupRegisterPromoteOAuth', err);
            setStatus('err');
            setMessage(err instanceof Error ? err.message : 'Could not complete registration. Please try again.');
        }
    };

    if (status === 'done') {
        return wrapper(
            <div style={themedStyles.card.default}>
                <SignupProgress currentStep={5} />
                <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                    <img src="/logo-name.svg" alt="Calculist" style={{ height: '64px', width: 'auto', marginBottom: theme.spacing.md }} />
                </div>
                <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        backgroundColor: '#f0fdf4',
                        color: '#16a34a',
                        fontSize: '28px',
                        marginBottom: theme.spacing.md,
                    }}>
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <h1 style={{ ...themedStyles.text.heading, marginTop: 0, marginBottom: theme.spacing.sm }}>You're all set!</h1>
                    <p style={{ ...themedStyles.text.body, marginTop: 0 }}>Your account is ready. Sign in to get started.</p>
                </div>
                <Link
                    to="/auth/login"
                    style={{ ...themedStyles.button.primary, display: 'block', textAlign: 'center', textDecoration: 'none' }}
                >
                    Sign In
                </Link>
            </div>
        );
    }

    if (status === 'err') {
        return wrapper(
            <div style={themedStyles.card.default}>
                <p style={themedStyles.text.body}>{message}</p>
                <Link to="/auth/register" style={{ color: theme.colors.primary }}>Try again</Link>
            </div>
        );
    }

    return wrapper(
        <div style={{ ...themedStyles.card.default, maxWidth: '560px' }}>
            <SignupProgress currentStep={3} />

            <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                <img src="/logo-name.svg" alt="Calculist" style={{ height: '64px', width: 'auto', marginBottom: theme.spacing.md }} />
                <h1 style={{ ...themedStyles.text.heading, marginTop: 0, marginBottom: theme.spacing.xs }}>Review &amp; Accept Terms</h1>
                <p style={{ ...themedStyles.text.muted, marginTop: 0 }}>Almost done — please review and accept the terms to finish creating your account.</p>
            </div>

            <div style={{
                maxHeight: '320px',
                overflowY: 'auto',
                border: `1px solid ${theme.colors.border.default}`,
                borderRadius: theme.borderRadius.lg,
                padding: theme.spacing.md,
                marginBottom: theme.spacing.lg,
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.text.secondary,
                lineHeight: 1.6,
            }}>
                <TermsAndConditionsContent />
            </div>

            <div style={{ marginBottom: theme.spacing.lg }}>
                <CustomToggle
                    checked={acceptTerms}
                    onChange={setAcceptTerms}
                    label="I have read and accept the Terms and Conditions"
                    disabled={status === 'submitting'}
                />
            </div>

            <button
                type="button"
                onClick={() => void handleAccept()}
                disabled={!acceptTerms || status === 'submitting'}
                style={{
                    ...themedStyles.button.primary,
                    ...(!acceptTerms || status === 'submitting' ? themedStyles.button.disabled : {}),
                    width: '100%',
                    padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                }}
            >
                {status === 'submitting' ? 'Creating your account…' : 'Accept & Create Account'}
            </button>
        </div>
    );
};
